<template>
  <div
    v-if="show"
    class="modal fade show d-block custom-backdrop"
    tabindex="-1"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content modal-scroll px-3 py-4">
        <!-- HEADER -->
        <div class="text-center mb-3">
          <i :class="[formMeta.icon, 'login-icon']"></i>

          <h4 class="mt-2 mb-0">
            {{ formMeta.title }}
          </h4>
        </div>

        <form
          v-if="currentForm === 'profile'"
          @submit.prevent="handleProfileUpdate"
        >
          <button
            type="button"
            class="btn-close position-absolute top-0 end-0 m-2"
            aria-label="Close"
            @click="closeModal"
          ></button>
          <!-- NAME -->
          <div class="mb-3 input-group">
            <span class="input-group-text bg-white border-end-0">
              <i class="bi bi-person-fill text-muted"></i>
            </span>

            <input
              v-model="name"
              type="text"
              class="form-control border-start-0 no-radius"
              placeholder="Name"
              required
            />
          </div>

          <!-- PHONE -->
          <div class="mb-3 input-group">
            <span class="input-group-text bg-white border-end-0">
              <i class="bi bi-telephone-fill text-muted"></i>
            </span>

            <input
              v-model="phone"
              type="text"
              class="form-control border-start-0 no-radius"
              placeholder="Phone Number"
            />
          </div>

          <!-- PASSWORD -->
          <div class="mb-3 input-group">
            <span class="input-group-text bg-white border-end-0">
              <i class="bi bi-lock-fill text-muted"></i>
            </span>

            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              class="form-control border-start-0 no-radius"
              placeholder="Password"
            />

            <button
              type="button"
              class="btn btn-outline-secondary border-start-0"
              @click="toggleShowPassword"
            >
              <i
                :class="
                  showPassword ? 'bi bi-eye-slash-fill' : 'bi bi-eye-fill'
                "
              ></i>
            </button>
          </div>

          <!-- UPDATE -->
          <button type="submit" class="btn loginuserbtn w-100">Update</button>
        </form>

        <!-- ========================================== -->
        <!-- ADDRESS FORM -->
        <!-- ========================================== -->

        <form
          v-if="currentForm === 'address'"
          @submit.prevent="handleUpdateAddress"
        >
          <button
            type="button"
            class="btn-close position-absolute top-0 end-0 m-2"
            aria-label="Close"
            @click="closeModal"
          ></button>
          <!-- ADDRESS -->
          <div class="mb-3">
            <input
              v-model="address"
              type="text"
              class="form-control"
              placeholder="Address"
              required
            />
          </div>

          <!-- STREET -->
          <div class="mb-3">
            <input
              v-model="street"
              type="text"
              class="form-control"
              placeholder="Street"
              required
            />
          </div>

          <!-- CITY -->
          <div class="mb-3">
            <input
              v-model="city"
              type="text"
              class="form-control"
              placeholder="City"
              required
            />
          </div>

          <!-- COUNTRY -->
          <div class="mb-3">
            <input
              v-model="country"
              type="text"
              class="form-control"
              placeholder="Country"
              required
            />
          </div>

          <!-- UPDATE -->
          <button type="submit" class="btn loginuserbtn w-100">Update</button>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import Config from "../Helpers/Config.js";

const API_BASE_URL = Config.API_BASE_URL;

// ==================================================
// EMITS
// ==================================================

// eslint-disable-next-line no-undef
const emit = defineEmits(["toast", "close", "updated", "logout"]);

// ==================================================
// PROPS
// ==================================================

// eslint-disable-next-line no-undef
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },

  currentForm: {
    type: String,
    default: "profile",
  },

  userDetails: {
    type: Object,
    default: () => ({}),
  },
});

// ==================================================
// PROFILE
// ==================================================

const name = ref("");
const phone = ref("");
const password = ref("");
const showPassword = ref(false);

// ==================================================
// ADDRESS
// ==================================================

const address = ref("");
const street = ref("");
const city = ref("");
const country = ref("");

// ==================================================
// STATUS
// ==================================================

const error = ref("");
const success = ref("");

// ==================================================
// CURRENT FORM
// ==================================================

const currentForm = computed(() => {
  return props.currentForm;
});

// ==================================================
// FORM META
// ==================================================

const formMeta = computed(() => {
  switch (currentForm.value) {
    case "profile":
      return {
        title: "Update Profile",
        icon: "bi bi-person-circle",
      };

    case "address":
      return {
        title: "Update Address",
        icon: "bi bi-geo-alt",
      };

    default:
      return {
        title: "Update",
        icon: "bi bi-pencil-square",
      };
  }
});

// ==================================================
// PASSWORD VISIBILITY
// ==================================================

function toggleShowPassword() {
  showPassword.value = !showPassword.value;
}

// ==================================================
// LOAD USER DETAILS INTO FORM
// ==================================================

watch(
  () => [props.show, props.userDetails, props.currentForm],

  ([isShown, details, form]) => {
    if (!isShown || !details) {
      return;
    }

    console.log("UpdateData userDetails:", details);
    console.log("UpdateData currentForm:", form);

    // ----------------------------------------------
    // PROFILE
    // ----------------------------------------------

    if (form === "profile") {
      name.value = details.firstname || "";

      phone.value = details.phone_number || "";

      password.value = "";

      showPassword.value = false;
    }

    // ----------------------------------------------
    // ADDRESS
    // ----------------------------------------------

    if (form === "address") {
      address.value = details.address || "";

      street.value = details.street || "";

      city.value = details.city || "";

      country.value = details.country || "";
    }
  },

  {
    immediate: true,
    deep: true,
  },
);

// ==================================================
// LOGOUT
// ==================================================

function logout() {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("user_data");

  emit("logout");

  window.location.href = "/";
}

// ==================================================
// PROFILE UPDATE
// ==================================================

async function handleProfileUpdate() {
  error.value = "";
  success.value = "";

  try {
    const token = localStorage.getItem("auth_token");
    const storedUser = localStorage.getItem("user_data");

    if (!token || !storedUser) {
      throw new Error("User authentication data not found");
    }

    const userData = JSON.parse(storedUser);

    const response = await fetch(`${API_BASE_URL}/update_user`, {
      method: "POST",

      headers: {
        "Content-Type": "application/json",
        'Authorization': `PRIMEPAY ${token}`,
        'user': userData.id
      },

      body: JSON.stringify({
        email: userData.email,
        firstname: name.value,
        phone_number: phone.value,
        password: password.value,
      }),
    });

    // ----------------------------------------------
    // UNAUTHORIZED
    // ----------------------------------------------

    if (response.status === 401) {
      logout();
      return;
    }

    const result = await response.json();

    // ----------------------------------------------
    // ERROR
    // ----------------------------------------------

    if (!response.ok || result.success === "0") {
      throw new Error(result.errorDesc || "Profile update failed");
    }

    // ----------------------------------------------
    // SUCCESS
    // ----------------------------------------------

    success.value = "Profile updated successfully!";

    emit("toast", {
      message: success.value,
      type: "success",
    });

    emit("updated");

    closeModal();
  } catch (err) {
    console.error("Profile update error:", err);

    error.value = err.message || "Unexpected error";

    emit("toast", {
      message: error.value,
      type: "error",
    });
  }
}

// ==================================================
// ADDRESS UPDATE
// ==================================================

async function handleUpdateAddress() {
  error.value = "";
  success.value = "";

  try {
    const token = localStorage.getItem("auth_token");
    const storedUser = localStorage.getItem("user_data");

    if (!token || !storedUser) {
      throw new Error("User authentication data not found");
    }

    const userData = JSON.parse(storedUser);

    const response = await fetch(`${API_BASE_URL}/update_address`, {
      method: "POST",

      headers: {
        "Content-Type": "application/json",
        'Authorization': `PRIMEPAY ${token}`,
        'user': userData.id
      },

      body: JSON.stringify({
        email: userData.email,
        address: address.value,
        street: street.value,
        city: city.value,
        country: country.value,
      }),
    });

    // ----------------------------------------------
    // UNAUTHORIZED
    // ----------------------------------------------

    if (response.status === 401) {
      logout();
      return;
    }

    const result = await response.json();

    // ----------------------------------------------
    // ERROR
    // ----------------------------------------------

    if (!response.ok || result.success === "0") {
      throw new Error(result.errorDesc || "Address update failed");
    }

    // ----------------------------------------------
    // SUCCESS
    // ----------------------------------------------

    success.value = "Address updated successfully!";

    emit("toast", {
      message: success.value,
      type: "success",
    });

    emit("updated");

    closeModal();
  } catch (err) {
    console.error("Address update error:", err);

    error.value = err.message || "Unexpected error";

    emit("toast", {
      message: error.value,
      type: "error",
    });
  }
}

// ==================================================
// CLOSE MODAL
// ==================================================

function closeModal() {
  emit("close");
}
</script>

<style scoped>
.custom-backdrop {
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 4000;
}

.login-icon {
  font-size: 3rem;
  color: var(--primary-color);
}

.no-radius {
  border-radius: 0 !important;
  box-shadow: none !important;
}

.input-group-text {
  border-radius: 0;
}

button:focus {
  box-shadow: none !important;
}

a {
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.96);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal {
  z-index: 1050;
  animation: fadeIn 0.3s ease-out;
}

.loginuserbtn {
  background-color: var(--primary-color);
  color: #fff;
}

.modal-scroll {
  max-height: 80vh;
  overflow-y: auto;
  padding-right: 10px;
}
.form-control:focus,
.form-control:focus-visible,
.password-toggle:focus,
.password-toggle:focus-visible,
.input-group-text:focus {
  box-shadow: none !important;
  outline: none !important;
  border-color: #dee2e6 !important;
}
</style>
